/**
 * +field: bounties (light quests, docs/design/zones.md "Bounties"): each zone's short intro chain
 * and its repeatable field bounties. Pure state transitions on a HeroCharacter's BountyState; the
 * caller (field.ts) pays the rewards.
 *
 * Data: the chain, bounties and rewards are authored in @keystone/content (zones/, `ZONES`);
 * `zoneQuestsFrom` adapts them to the `ZoneQuests` shape the logic below reads.
 */
import { type BountyStep as ContentStep, type ZoneDef, ZONES, getContentBundle } from '@keystone/content';
import { fieldAreaAt } from '@keystone/sim';
import type { BountyState, HeroCharacter } from './types';

export interface QuestReward {
  xp: number;
  gold: number;
}

/** Fields every chain step has. */
interface StepBase {
  id: string;
  title: string;
  reward: QuestReward;
  /** Where the tracker points (any map). */
  waypoint?: { mapId: string; pos: { x: number; y: number } };
  /** Web-only: a panel to open when the step completes (rides along on the completion). */
  panel?: string;
}

/** One step of a zone's intro chain. */
export type ChainStep = StepBase &
  (
    /** Talk to an NPC (bountyTalk) while standing on its map (`mapId`). */
    | { kind: 'talk'; npcId: string; mapId: string }
    /** Walk onto a map (fieldMove); with `areaId`, into that area (or a sub-area of that region) of it. */
    | { kind: 'goto'; mapId: string; areaId?: string }
    /** Kill `count` of these mobs (fieldKills; on `mapId` when set). */
    | { kind: 'kill'; npcIds: string[]; count: number; mapId?: string }
    /** Enter a levelled dungeon (dungeonEnter). */
    | { kind: 'enter'; dungeonId: string }
    /** Clear a levelled dungeon (dungeonClear). */
    | { kind: 'clear'; dungeonId: string }
    /** Kill `count` mobs (any) while Auto plays the hero (fieldKills with `auto`). */
    | { kind: 'auto'; count: number }
    /** Reach hero level `level` (checked after every XP gain and as soon as the step is current). */
    | { kind: 'level'; level: number }
  );

/** A repeatable field bounty (accept and turn in at the town's bounty board). */
export interface BountyDef {
  id: string;
  title: string;
  /** Field the mobs live on (UI hint; kills count on any map). */
  mapId: string;
  npcIds: string[];
  count: number;
  reward: QuestReward;
  /** Recommended level (UI). */
  minLevel?: number;
  /** +zone: the region its mobs live in (UI). */
  areaId?: string;
  /** +zone: the board NPC posting it (absent = the zone's boardNpcId). */
  boardNpcId?: string;
  /** +zone: the safe area it is taken / turned in at (absent = the town area). */
  boardAreaId?: string;
}

export interface ZoneQuests {
  zoneId: string;
  /** The zone's town map (bounties are accepted and turned in here). */
  townMapId: string;
  /** +zone: the town's area on `townMapId` (the hero must stand in it). */
  townAreaId?: string;
  /** +zone: board bounties unlock when the chain reaches this step (absent = from the start). */
  boardStepId?: string;
  /** +zone: the Dungeon Board opens once this chain step is done (absent = always). */
  dungeonBoardAfterStepId?: string;
  /** NPC that hands out the repeatables. */
  boardNpcId: string;
  chain: ChainStep[];
  bounties: BountyDef[];
}

/** A content chain step (packages/content/src/zones) in the shape the logic below reads. */
export function chainStep(st: ContentStep): ChainStep {
  const x = st as ContentStep & { level?: number; waypoint?: StepBase['waypoint']; panel?: string; dungeonId?: string };
  const base: StepBase = { id: st.id, title: st.title, reward: { ...st.reward } };
  if (x.waypoint) base.waypoint = { mapId: x.waypoint.mapId, pos: { ...x.waypoint.pos } };
  if (x.panel) base.panel = x.panel;
  const kind = st.kind as string;
  switch (kind) {
    case 'talk':
      return { ...base, kind: 'talk', npcId: st.target, mapId: st.mapId };
    case 'kill':
      return { ...base, kind: 'kill', npcIds: [...(st.npcIds ?? [st.target])], count: st.count, mapId: st.mapId };
    case 'dungeon':
    case 'clear':
      return { ...base, kind: 'clear', dungeonId: x.dungeonId ?? st.target };
    case 'enter':
      return { ...base, kind: 'enter', dungeonId: x.dungeonId ?? st.target };
    case 'goto':
      return { ...base, kind: 'goto', mapId: st.target || st.mapId, ...(st.areaId ? { areaId: st.areaId } : {}) };
    case 'auto':
      return { ...base, kind: 'auto', count: Math.max(1, st.count ?? 1) };
    case 'level':
      return { ...base, kind: 'level', level: x.level ?? (Number(st.target) || st.count) };
    default:
      throw new Error(`unknown chain step kind ${kind} (${st.id})`);
  }
}

/** The zone data adapter: content's ZoneDef → ZoneQuests. */
export function zoneQuestsFrom(z: ZoneDef): ZoneQuests {
  return {
    zoneId: z.id,
    townMapId: z.townId,
    ...(z.townAreaId ? { townAreaId: z.townAreaId } : {}),
    ...(z.boardStepId ? { boardStepId: z.boardStepId } : {}),
    ...(z.dungeonBoardAfterStepId ? { dungeonBoardAfterStepId: z.dungeonBoardAfterStepId } : {}),
    boardNpcId: 'bounty_board',
    chain: z.chain.map(chainStep),
    bounties: z.bounties.map((b) => ({ id: b.id, title: b.title, mapId: b.mapId, npcIds: [...(b.npcIds ?? [b.target])], count: b.count, reward: { ...b.reward }, minLevel: b.minLevel, ...(b.areaId ? { areaId: b.areaId } : {}), ...(b.boardNpcId ? { boardNpcId: b.boardNpcId } : {}), ...(b.boardAreaId ? { boardAreaId: b.boardAreaId } : {}) })),
  };
}

export const ZONE_QUESTS: Readonly<Record<string, ZoneQuests>> = Object.fromEntries(Object.values(ZONES).map((z) => [z.id, zoneQuestsFrom(z)]));
/** Mossvale (1–10). */
export const MOSSVALE_QUESTS: ZoneQuests = ZONE_QUESTS['mossvale']!;

export function zoneQuests(zoneId: string): ZoneQuests | undefined {
  return ZONE_QUESTS[zoneId];
}

/** Every repeatable bounty of every zone, by id. */
export function bountyDef(id: string): (BountyDef & { zoneId: string }) | undefined {
  for (const z of Object.values(ZONE_QUESTS)) {
    const b = z.bounties.find((x) => x.id === id);
    if (b) return { ...b, zoneId: z.zoneId };
  }
  return undefined;
}

export function emptyBountyState(zoneIds: readonly string[] = Object.keys(ZONE_QUESTS)): BountyState {
  const chain: Record<string, number> = {};
  const chainProgress: Record<string, number> = {};
  for (const z of zoneIds) {
    chain[z] = 0;
    chainProgress[z] = 0;
  }
  return { chain, chainProgress, active: {}, completed: {} };
}

/** The zone's current chain step (null when the chain is done or the zone has none). */
export function currentChainStep(b: BountyState, zoneId: string): ChainStep | null {
  const z = zoneQuests(zoneId);
  return z?.chain[b.chain[zoneId] ?? 0] ?? null;
}

/** A finished quest step or turned-in bounty (the web's toast / tracker). */
export interface QuestCompletion {
  kind: 'chain' | 'bounty';
  id: string;
  title: string;
  reward: QuestReward;
  /** The step's web panel to open (chain steps that name one). */
  panel?: string;
}

/** Progress made on an active repeatable bounty. */
export interface BountyProgress {
  id: string;
  progress: number;
  count: number;
}

/** Complete the current chain step of `zoneId` (mutates `b`). */
function completeStep(b: BountyState, zoneId: string, step: ChainStep, out: QuestCompletion[]): void {
  out.push({ kind: 'chain', id: step.id, title: step.title, reward: { ...step.reward }, ...(step.panel ? { panel: step.panel } : {}) });
  b.chain[zoneId] = (b.chain[zoneId] ?? 0) + 1;
  b.chainProgress[zoneId] = 0;
}

/**
 * Complete every current step that is already satisfied: a 'goto' for the map the hero stands on,
 * a 'level' the hero has reached. Call after any change (the caller pays, then settles again with
 * the new level, since rewards can level the hero).
 */
export function bountySettle(ch: HeroCharacter, zoneId: string, heroLevel: number): QuestCompletion[] {
  const out: QuestCompletion[] = [];
  for (let i = 0; i < 32; i++) {
    const s = currentChainStep(ch.bounties, zoneId);
    if (!s) break;
    if ((s.kind === 'goto' && s.mapId === ch.mapId && (!s.areaId || inArea(ch, s.areaId))) || (s.kind === 'level' && heroLevel >= s.level)) completeStep(ch.bounties, zoneId, s, out);
    else break;
  }
  return out;
}

/** +zone: the hero's saved position is in `areaId` (that area, or any sub-area of that region) on its map. */
export function inArea(ch: Pick<HeroCharacter, 'mapId' | 'pos'>, areaId: string): boolean {
  if (!ch.pos) return false;
  const map = getContentBundle().dungeons.find((d) => d.id === ch.mapId);
  const a = fieldAreaAt(map?.field, ch.pos);
  return !!a && (a.id === areaId || a.region === areaId);
}

/**
 * Kills (one entry per kill) advance the zone's kill step (and its 'auto' step when Auto made them)
 * and every active bounty that counts them. Mutates `ch`.
 */
export function bountyKills(ch: HeroCharacter, zoneId: string, kills: readonly { npcId: string; mapId: string }[], auto = false): { completed: QuestCompletion[]; bounties: BountyProgress[] } {
  const completed: QuestCompletion[] = [];
  const b = ch.bounties;
  const touched = new Set<string>();
  for (const k of kills) {
    const s = currentChainStep(b, zoneId);
    const counts = (s?.kind === 'kill' && s.npcIds.includes(k.npcId) && (!s.mapId || s.mapId === k.mapId)) || (s?.kind === 'auto' && auto);
    if (s && counts && (s.kind === 'kill' || s.kind === 'auto')) {
      b.chainProgress[zoneId] = (b.chainProgress[zoneId] ?? 0) + 1;
      if (b.chainProgress[zoneId]! >= s.count) completeStep(b, zoneId, s, completed);
    }
    for (const id of Object.keys(b.active).sort()) {
      const def = bountyDef(id);
      if (!def || !def.npcIds.includes(k.npcId) || b.active[id]! >= def.count) continue;
      b.active[id] = b.active[id]! + 1;
      touched.add(id);
    }
  }
  const bounties = [...touched].map((id) => ({ id, progress: b.active[id]!, count: bountyDef(id)!.count }));
  return { completed, bounties };
}

/**
 * Talk to `npcId`: completes the zone's current 'talk' step for that NPC. The hero must stand on
 * the NPC's map (`npcMapId`, resolved from the map data by the caller; default: the step's map).
 */
export function bountyTalk(ch: HeroCharacter, zoneId: string, npcId: string, npcMapId?: string): QuestCompletion[] | null {
  const s = currentChainStep(ch.bounties, zoneId);
  if (s?.kind !== 'talk' || s.npcId !== npcId || (npcMapId ?? s.mapId) !== ch.mapId) return null;
  const out: QuestCompletion[] = [];
  completeStep(ch.bounties, zoneId, s, out);
  return out;
}

/** A levelled dungeon was entered / cleared: completes a matching 'enter' / 'clear' step. */
export function bountyDungeon(ch: HeroCharacter, zoneId: string, dungeonId: string, what: 'enter' | 'clear'): QuestCompletion[] {
  const s = currentChainStep(ch.bounties, zoneId);
  if (s?.kind !== what || s.dungeonId !== dungeonId) return [];
  const out: QuestCompletion[] = [];
  completeStep(ch.bounties, zoneId, s, out);
  return out;
}

/** Arriving on a map (fieldMove): settles a 'goto' (and a reached 'level') step. */
export const bountyArrive = (ch: HeroCharacter, zoneId: string, heroLevel: number): QuestCompletion[] => bountySettle(ch, zoneId, heroLevel);

/** @deprecated use bountyDungeon(ch, zoneId, dungeonId, 'clear'). */
export const bountyClear = (ch: HeroCharacter, zoneId: string, dungeonId: string): QuestCompletion[] => bountyDungeon(ch, zoneId, dungeonId, 'clear');

/** The hero stands in its zone's town (the town map; on a zone map, inside the town area). */
function inTown(ch: HeroCharacter, zoneId: string): boolean {
  const z = zoneQuests(zoneId);
  if (!z || ch.mapId !== z.townMapId) return false;
  return !z.townAreaId || inArea(ch, z.townAreaId);
}

/** +zone: the hero stands where this bounty's board is (its boardAreaId on the zone map, else in town). */
export function atBountyBoard(ch: HeroCharacter, bountyId: string): boolean {
  const def = bountyDef(bountyId);
  if (!def) return false;
  if (!def.boardAreaId) return inTown(ch, def.zoneId);
  const z = zoneQuests(def.zoneId);
  return !!z && ch.mapId === z.townMapId && inArea(ch, def.boardAreaId);
}

export type BountyError = 'unknownBounty' | 'notInTown' | 'alreadyActive' | 'notActive' | 'notReady' | 'tooLow' | 'locked';

/**
 * +zone: the Bounty Board has work for this hero (the main quest reached its board step). Until
 * then the board, its marker and the tracker's bounty rows stay out of the way of the main quest.
 */
export function boardUnlocked(b: BountyState, zoneId: string): boolean {
  const z = zoneQuests(zoneId);
  if (!z?.boardStepId) return true;
  const at = z.chain.findIndex((s) => s.id === z.boardStepId);
  return at < 0 || (b.chain[zoneId] ?? 0) >= at;
}

/** +zone: the Dungeon Board (keystones) is open for this hero (its gating chain step is done). */
export function dungeonBoardOpen(b: BountyState, zoneId: string): boolean {
  const z = zoneQuests(zoneId);
  if (!z?.dungeonBoardAfterStepId) return true;
  const at = z.chain.findIndex((s) => s.id === z.dungeonBoardAfterStepId);
  return at < 0 || (b.chain[zoneId] ?? 0) > at;
}

/** Accept a repeatable bounty (hero on its zone's town map, at its minLevel). Mutates `ch`. */
export function acceptBounty(ch: HeroCharacter, bountyId: string, heroLevel = Infinity): BountyError | null {
  const def = bountyDef(bountyId);
  if (!def) return 'unknownBounty';
  if (!boardUnlocked(ch.bounties, def.zoneId)) return 'locked';
  if (heroLevel < (def.minLevel ?? 1)) return 'tooLow';
  if (!atBountyBoard(ch, bountyId)) return 'notInTown';
  if (ch.bounties.active[bountyId] !== undefined) return 'alreadyActive';
  ch.bounties.active[bountyId] = 0;
  return null;
}

/** Turn in a finished bounty (hero on its zone's town map): returns the completion (the caller pays). Mutates `ch`. */
export function turnInBounty(ch: HeroCharacter, bountyId: string): QuestCompletion | BountyError {
  const def = bountyDef(bountyId);
  if (!def) return 'unknownBounty';
  if (!atBountyBoard(ch, bountyId)) return 'notInTown';
  const have = ch.bounties.active[bountyId];
  if (have === undefined) return 'notActive';
  if (have < def.count) return 'notReady';
  delete ch.bounties.active[bountyId];
  ch.bounties.completed[bountyId] = (ch.bounties.completed[bountyId] ?? 0) + 1;
  return { kind: 'bounty', id: def.id, title: def.title, reward: { ...def.reward } };
}
